// What an auth email link (password reset, sign-up confirmation) put in the
// URL when the app loaded. Read once, at import, before the Supabase client
// works on the URL: lib/supabase.js imports this module ahead of creating the
// client.
//
// Why the pages need it: auth-js turns a failed link's
// #error=access_denied&error_code=otp_expired&... into an error it keeps to
// itself (getSession() still answers { session: null, error: null }), and on
// a failed link it keeps whatever session was already there. So the reset page
// showed its form to an expired link (ending in "Auth session missing!"), or
// to a coach who was simply signed in, and the login page showed nothing at
// all. A good recovery link's access token is kept too, so the reset page can
// tell a recovery session from an ordinary one.

export function parseAuthRedirect(location) {
  if (!location) return null;
  const hash = new URLSearchParams(String(location.hash || '').replace(/^#/, ''));
  const query = new URLSearchParams(String(location.search || '').replace(/^\?/, ''));
  const pick = (key) => hash.get(key) || query.get(key) || null;

  const error = pick('error');
  const errorCode = pick('error_code');
  const errorDescription = pick('error_description');
  const accessToken = hash.get('access_token') || null;
  if (!error && !errorCode && !errorDescription && !accessToken) return null;

  return {
    path: location.pathname || '/',
    type: pick('type'),
    accessToken,
    failed: Boolean(error || errorCode || errorDescription),
    errorCode: errorCode || error || null,
  };
}

const bootAuthRedirect = typeof window !== 'undefined' ? parseAuthRedirect(window.location) : null;

// The link the app was opened with, when it was opened on `path`.
export function getBootAuthRedirect(path) {
  if (!bootAuthRedirect) return null;
  if (path && !bootAuthRedirect.path.startsWith(path)) return null;
  return bootAuthRedirect;
}

// Takes a failed link's leftovers out of the address bar (auth-js leaves
// them), keeping the path and any other query.
export function clearAuthRedirectFromUrl() {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    ['error', 'error_code', 'error_description'].forEach((key) => url.searchParams.delete(key));
    url.hash = '';
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}`);
  } catch { /* leave the URL as it is */ }
}
